'use client';

import { useTranslation } from 'react-i18next';
import { useMediaSource, type MediaCategory, type MediaItem } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { sectionPad, sectionInner } from '../_shared/styles';
import { VideoRail } from './VideoRail';
import { VideoCard } from './VideoCard';

/**
 * /video index — hero rail + category nav + N source-keyed rails +
 * paginated grid. Drives the catalog-browse experience.
 */
export interface VideoIndexLayoutProps {
  title?: string;
  heroSource?: string;
  rails?: Array<{
    key: string;
    label: string;
    source: string;
    limit?: number;
    seeAllHref?: string;
  }>;
  gridSource?: string;
  gridLimit?: number;
  showCategories?: boolean;
  videoHrefTemplate?: string;
}

/** The stock trending / latest / editor-picks rails, built per render so
 *  a language switch relabels them. */
const defaultRails = (t: (key: string) => string) => [
  { key: 'trending', label: t('videoIndex.rail.trending'), source: 'trending', limit: 8 },
  { key: 'latest', label: t('videoIndex.rail.latest'), source: 'latest', limit: 8 },
  {
    key: 'editor-picks',
    label: t('videoIndex.rail.editorPicks'),
    source: 'editor-picks',
    limit: 8,
  },
];

export function VideoIndexLayout({
  title,
  heroSource = 'editor-picks',
  rails: authoredRails,
  gridSource = 'all',
  gridLimit = 24,
  showCategories = true,
  videoHrefTemplate = '/tube/{slug}',
}: VideoIndexLayoutProps) {
  const { t } = useTranslation('site-builder-blocks');
  const heading = title ?? t('shared.label.videos');
  const rails = authoredRails ?? defaultRails(t);
  const media = useMediaSource();

  const state = useAsyncResource(
    async (signal) => {
      if (!media) return null;
      const [hero, categories, gridList, ...railResults] = await Promise.all([
        media.videos.fetchSource({ source: heroSource, limit: 3, signal }),
        showCategories ? media.categories.list({ signal }) : Promise.resolve([] as MediaCategory[]),
        media.videos.fetchSource({ source: gridSource, limit: gridLimit, signal }),
        ...rails.map((r) =>
          media.videos.fetchSource({ source: r.source, limit: r.limit ?? 8, signal }),
        ),
      ]);
      return {
        hero: hero?.items ?? [],
        categories,
        grid: gridList?.items ?? [],
        rails: rails.map((r, i) => ({ ...r, items: railResults[i]?.items ?? [] })),
      };
    },
    [media, heroSource, gridSource, gridLimit, showCategories, JSON.stringify(rails)],
  );

  if (!media) {
    return (
      <section style={sectionPad}>
        <div style={sectionInner}>
          <AdapterPlaceholder blockType="VideoIndexLayout" adapterName="MediaSource" />
        </div>
      </section>
    );
  }

  if (state.kind !== 'loaded') return null;
  const { hero, categories, grid, rails: resolvedRails } = state.data;

  return (
    <section style={sectionPad}>
      <div style={sectionInner}>
        <h1 style={{ margin: 0, fontSize: 'clamp(24px, 4vw, 32px)', fontWeight: 700 }}>
          {heading}
        </h1>

        {hero.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <VideoRail items={hero} variant="cinematic" videoHrefTemplate={videoHrefTemplate} />
          </div>
        )}

        {showCategories && categories.length > 0 && (
          <nav
            aria-label={t('shared.label.categories')}
            style={{
              marginTop: 24,
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              paddingBottom: 16,
              borderBottom: '1px solid var(--color-border, rgba(0,0,0,0.08))',
            }}
          >
            {categories.map((c) => (
              <a
                key={c.slug}
                href={`?category=${encodeURIComponent(c.slug)}`}
                style={{
                  padding: 'var(--space-1-5, 6px) var(--space-3-5, 14px)',
                  borderRadius: 999,
                  border: '1px solid var(--color-border, rgba(0,0,0,0.12))',
                  fontSize: '13px',
                  fontWeight: 500,
                  textDecoration: 'none',
                  color: 'inherit',
                  background: 'var(--color-surface-card, #fff)',
                }}
              >
                {c.name}
              </a>
            ))}
          </nav>
        )}

        {resolvedRails.map((r) =>
          r.items.length > 0 ? (
            <div key={r.key} style={{ marginTop: 32 }}>
              <VideoRail title={r.label} items={r.items} videoHrefTemplate={videoHrefTemplate} />
            </div>
          ) : null,
        )}

        {grid.length > 0 && (
          <div style={{ marginTop: 40 }}>
            <h2
              style={{ margin: '0 0 16px', fontSize: 'var(--font-size-lg, 18px)', fontWeight: 600 }}
            >
              {t('videoIndex.allVideos')}
            </h2>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))',
                gap: 16,
              }}
            >
              {grid.map((item: MediaItem) => (
                <VideoCard key={item.id} item={item} hrefTemplate={videoHrefTemplate} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
